/**
 * Authentication context.
 *
 * Owns:
 * - Current authenticated user/org/role state
 * - Pending email verification hold state
 * - Login / logout / register actions
 * - Session bootstrap from stored tokens
 */

import { createContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { api, ApiError, clearTokens, hasAccessToken, restoreTokensFromStorage, setTokens } from './api-client';
import { AuthTokenPair, MemberRole, PartnerStatus, PartnerUserRole } from '@accestra/types';
import { changeAppLanguage, resolveInitialLocale } from '../i18n/i18n';
import { isSupportedLocale, type AppLocale } from '../i18n/localeTypes';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  preferredLocale: AppLocale | null;
  isEmailVerified: boolean;
}

export interface AuthOrg {
  id: string;
  slug: string;
  name: string;
  defaultLocale: AppLocale | null;
  isDemoWorkspace: boolean;
}

export interface AuthPartnerAccess {
  partnerId: string;
  role: PartnerUserRole;
  partnerName: string;
  partnerStatus: PartnerStatus;
  referralCode: string;
}

export interface VerificationEmailStatus {
  deliveryStatus: 'sent' | 'skipped' | 'failed';
  deliveryReason: string | null;
  expiresAt: string | null;
}

export interface PendingVerificationState {
  user: AuthUser;
  organisation: AuthOrg;
  role: MemberRole;
  nextPath: string;
  verificationEmail: VerificationEmailStatus;
}

export interface WorkspaceSelectionOption {
  organisation: AuthOrg;
  role: MemberRole;
}

export interface PendingWorkspaceSelectionState {
  user: AuthUser;
  nextPath: string;
  selectionToken: string;
  workspaces: WorkspaceSelectionOption[];
}

export interface AuthState {
  user: AuthUser | null;
  org: AuthOrg | null;
  role: MemberRole | null;
  workspaces: WorkspaceSelectionOption[];
  partnerAccess: AuthPartnerAccess | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isBootstrapping: boolean;
  pendingVerification: PendingVerificationState | null;
  pendingWorkspaceSelection: PendingWorkspaceSelectionState | null;
  hasPendingVerification: boolean;
  hasPendingWorkspaceSelection: boolean;
  hasPartnerAccess: boolean;
}

export interface AuthActions {
  login(email: string, password: string, nextPath?: string): Promise<{ status: 'authenticated' | 'verification_required' | 'workspace_selection_required' }>;
  register(input: {
    email: string;
    fullName: string;
    password: string;
    organisationName: string;
    organisationSlug: string;
    partnerReferralCode?: string | null;
  }, nextPath?: string): Promise<{ status: 'authenticated' | 'verification_required' }>;
  establishSession(input: {
    user: AuthUser;
    organisation: AuthOrg;
    role: MemberRole;
    workspaces?: WorkspaceSelectionOption[];
    partnerAccess?: AuthPartnerAccess | null;
    tokens: AuthTokenPair;
  }): void;
  setPendingVerification(input: PendingVerificationState): void;
  setPendingWorkspaceSelection(input: PendingWorkspaceSelectionState): void;
  selectWorkspace(organisationId: string): Promise<{ status: 'authenticated' | 'verification_required' }>;
  switchWorkspace(organisationId: string): Promise<void>;
  createWorkspace(input: {
    organisationName: string;
    organisationSlug?: string | null;
  }): Promise<void>;
  resendVerificationEmail(): Promise<VerificationEmailStatus>;
  confirmEmailVerification(token: string): Promise<{ nextPath: string }>;
  clearPendingVerification(): void;
  clearPendingWorkspaceSelection(): void;
  syncLocalePreferences(input: {
    preferredLocale?: AppLocale | null;
    defaultLocale?: AppLocale | null;
  }): void;
  logout(): Promise<void>;
}

export type AuthContextValue = AuthState & AuthActions;
type RestoredAuthState = Pick<AuthState, 'user' | 'org' | 'role' | 'workspaces' | 'partnerAccess'>;

export const AuthContext = createContext<AuthContextValue | null>(null);

const LS_AUTH_STATE = 'accestra_auth_state';
const LS_PENDING_VERIFICATION = 'accestra_pending_verification';
const LS_PENDING_WORKSPACE_SELECTION = 'accestra_pending_workspace_selection';

interface PersistedAuthState {
  user: AuthUser;
  org: AuthOrg;
  role: MemberRole;
  workspaces: WorkspaceSelectionOption[];
  partnerAccess: AuthPartnerAccess | null;
}

interface AuthenticatedResponseData {
  status: 'authenticated';
  user: AuthUser;
  organisation: AuthOrg;
  role: MemberRole;
  workspaces: WorkspaceSelectionOption[];
  partnerAccess: AuthPartnerAccess | null;
  tokens: AuthTokenPair;
}

interface VerificationRequiredResponseData {
  status: 'verification_required';
  user: AuthUser;
  organisation: AuthOrg;
  role: MemberRole;
  verificationEmail: VerificationEmailStatus;
}

interface WorkspaceSelectionRequiredResponseData {
  status: 'workspace_selection_required';
  user: AuthUser;
  workspaces: WorkspaceSelectionOption[];
  selectionToken: string;
}

type AuthFlowResponse =
  | AuthenticatedResponseData
  | VerificationRequiredResponseData
  | WorkspaceSelectionRequiredResponseData;

interface AuthBootstrapResponse {
  user: AuthUser;
  organisation: AuthOrg;
  role: MemberRole;
  workspaces: WorkspaceSelectionOption[];
  partnerAccess: AuthPartnerAccess | null;
}

interface ResendVerificationResponse {
  accepted: true;
  verificationEmail: VerificationEmailStatus;
}

function isMemberRole(value: unknown): value is MemberRole {
  return value === 'owner' || value === 'admin' || value === 'member';
}

function isPartnerUserRole(value: unknown): value is PartnerUserRole {
  return value === 'owner' || value === 'member';
}

function isPartnerStatus(value: unknown): value is PartnerStatus {
  return (
    value === 'applicant'
    || value === 'active'
    || value === 'paused'
    || value === 'rejected'
    || value === 'terminated'
  );
}

function emptyAuthState(): RestoredAuthState {
  return {
    user: null,
    org: null,
    role: null,
    workspaces: [],
    partnerAccess: null,
  };
}

function toAppLocale(value: unknown): AppLocale | null {
  return typeof value === 'string' && isSupportedLocale(value) ? value : null;
}

function toAuthOrg(value: Partial<AuthOrg> | null | undefined): AuthOrg | null {
  if (
    !value
    || typeof value.id !== 'string'
    || typeof value.slug !== 'string'
    || typeof value.name !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    slug: value.slug,
    name: value.name,
    defaultLocale: toAppLocale(value.defaultLocale),
    isDemoWorkspace: typeof value.isDemoWorkspace === 'boolean' ? value.isDemoWorkspace : false,
  };
}

function toWorkspaceSelectionOption(
  value: Partial<WorkspaceSelectionOption> | null | undefined,
): WorkspaceSelectionOption | null {
  if (!value || !isMemberRole(value.role)) {
    return null;
  }

  const organisation = toAuthOrg(value.organisation);
  if (!organisation) {
    return null;
  }

  return {
    organisation,
    role: value.role,
  };
}

function normalizeWorkspaceOptions(
  value: unknown,
  fallback?: { organisation: AuthOrg; role: MemberRole } | null,
): WorkspaceSelectionOption[] {
  const workspaces = Array.isArray(value)
    ? value
      .map((workspace) => toWorkspaceSelectionOption(workspace as Partial<WorkspaceSelectionOption>))
      .filter((workspace): workspace is WorkspaceSelectionOption => workspace !== null)
    : [];

  if (workspaces.length > 0) {
    return workspaces;
  }

  if (fallback) {
    return [{
      organisation: fallback.organisation,
      role: fallback.role,
    }];
  }

  return [];
}

function persistAuthState(
  user: AuthUser,
  org: AuthOrg,
  role: MemberRole,
  partnerAccess: AuthPartnerAccess | null,
  workspaces: WorkspaceSelectionOption[],
): void {
  try {
    localStorage.setItem(LS_AUTH_STATE, JSON.stringify({ user, org, role, workspaces, partnerAccess }));
  } catch {
    // Ignore storage failures.
  }
}

function clearPersistedAuthState(): void {
  try {
    localStorage.removeItem(LS_AUTH_STATE);
  } catch {
    // Ignore storage failures.
  }
}

function restorePersistedAuthState(): PersistedAuthState | null {
  try {
    const raw = localStorage.getItem(LS_AUTH_STATE);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      user?: Partial<AuthUser>;
      org?: Partial<AuthOrg>;
      role?: unknown;
      workspaces?: unknown;
      partnerAccess?: Partial<AuthPartnerAccess> | null;
    };

    const org = toAuthOrg(parsed.org);

    if (
      parsed.user &&
      typeof parsed.user.id === 'string' &&
      typeof parsed.user.email === 'string' &&
      typeof parsed.user.fullName === 'string' &&
      org &&
      isMemberRole(parsed.role)
    ) {
      return {
        user: {
          id: parsed.user.id,
          email: parsed.user.email,
          fullName: parsed.user.fullName,
          preferredLocale: toAppLocale(parsed.user.preferredLocale),
          isEmailVerified: typeof parsed.user.isEmailVerified === 'boolean' ? parsed.user.isEmailVerified : true,
        },
        org,
        role: parsed.role,
        workspaces: normalizeWorkspaceOptions(parsed.workspaces, {
          organisation: org,
          role: parsed.role,
        }),
        partnerAccess: (
          parsed.partnerAccess
          && typeof parsed.partnerAccess.partnerId === 'string'
          && typeof parsed.partnerAccess.partnerName === 'string'
          && typeof parsed.partnerAccess.referralCode === 'string'
          && isPartnerUserRole(parsed.partnerAccess.role)
          && isPartnerStatus(parsed.partnerAccess.partnerStatus)
        ) ? {
          partnerId: parsed.partnerAccess.partnerId,
          role: parsed.partnerAccess.role,
          partnerName: parsed.partnerAccess.partnerName,
          partnerStatus: parsed.partnerAccess.partnerStatus,
          referralCode: parsed.partnerAccess.referralCode,
        } : null,
      };
    }
  } catch {
    // Fall through to clearing bad state.
  }

  clearPersistedAuthState();
  return null;
}

function persistPendingVerification(state: PendingVerificationState): void {
  try {
    localStorage.setItem(LS_PENDING_VERIFICATION, JSON.stringify(state));
  } catch {
    // Ignore storage failures.
  }
}

function clearPersistedPendingVerification(): void {
  try {
    localStorage.removeItem(LS_PENDING_VERIFICATION);
  } catch {
    // Ignore storage failures.
  }
}

function restorePendingVerification(): PendingVerificationState | null {
  try {
    const raw = localStorage.getItem(LS_PENDING_VERIFICATION);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingVerificationState>;
    if (
      parsed.user &&
      typeof parsed.user.id === 'string' &&
      typeof parsed.user.email === 'string' &&
      typeof parsed.user.fullName === 'string' &&
      parsed.organisation &&
      typeof parsed.organisation.id === 'string' &&
      typeof parsed.organisation.slug === 'string' &&
      typeof parsed.organisation.name === 'string' &&
      isMemberRole(parsed.role) &&
      typeof parsed.nextPath === 'string' &&
      parsed.verificationEmail &&
      typeof parsed.verificationEmail.deliveryStatus === 'string'
    ) {
      return {
        user: {
          id: parsed.user.id,
          email: parsed.user.email,
          fullName: parsed.user.fullName,
          preferredLocale: toAppLocale(parsed.user.preferredLocale),
          isEmailVerified: typeof parsed.user.isEmailVerified === 'boolean' ? parsed.user.isEmailVerified : false,
        },
        organisation: {
          id: parsed.organisation.id,
          slug: parsed.organisation.slug,
          name: parsed.organisation.name,
          defaultLocale: toAppLocale(parsed.organisation.defaultLocale),
          isDemoWorkspace: typeof parsed.organisation.isDemoWorkspace === 'boolean' ? parsed.organisation.isDemoWorkspace : false,
        },
        role: parsed.role,
        nextPath: parsed.nextPath,
        verificationEmail: {
          deliveryStatus: parsed.verificationEmail.deliveryStatus,
          deliveryReason: parsed.verificationEmail.deliveryReason ?? null,
          expiresAt: parsed.verificationEmail.expiresAt ?? null,
        },
      };
    }
  } catch {
    // Fall through to clearing bad state.
  }

  clearPersistedPendingVerification();
  return null;
}

function persistPendingWorkspaceSelection(state: PendingWorkspaceSelectionState): void {
  try {
    localStorage.setItem(LS_PENDING_WORKSPACE_SELECTION, JSON.stringify(state));
  } catch {
    // Ignore storage failures.
  }
}

function clearPersistedPendingWorkspaceSelection(): void {
  try {
    localStorage.removeItem(LS_PENDING_WORKSPACE_SELECTION);
  } catch {
    // Ignore storage failures.
  }
}

function restorePendingWorkspaceSelection(): PendingWorkspaceSelectionState | null {
  try {
    const raw = localStorage.getItem(LS_PENDING_WORKSPACE_SELECTION);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingWorkspaceSelectionState>;
    const workspaces = Array.isArray(parsed.workspaces) ? parsed.workspaces : [];
    if (
      parsed.user &&
      typeof parsed.user.id === 'string' &&
      typeof parsed.user.email === 'string' &&
      typeof parsed.user.fullName === 'string' &&
      typeof parsed.selectionToken === 'string' &&
      typeof parsed.nextPath === 'string' &&
      workspaces.length > 0 &&
      workspaces.every((workspace) => (
        workspace
        && typeof workspace === 'object'
        && workspace.organisation
        && typeof workspace.organisation.id === 'string'
        && typeof workspace.organisation.slug === 'string'
        && typeof workspace.organisation.name === 'string'
        && isMemberRole(workspace.role)
      ))
    ) {
      return {
        user: {
          id: parsed.user.id,
          email: parsed.user.email,
          fullName: parsed.user.fullName,
          preferredLocale: toAppLocale(parsed.user.preferredLocale),
          isEmailVerified: typeof parsed.user.isEmailVerified === 'boolean' ? parsed.user.isEmailVerified : false,
        },
        selectionToken: parsed.selectionToken,
        nextPath: parsed.nextPath,
        workspaces: workspaces.map((workspace) => ({
          organisation: {
            id: workspace.organisation.id,
            slug: workspace.organisation.slug,
            name: workspace.organisation.name,
            defaultLocale: toAppLocale(workspace.organisation.defaultLocale),
            isDemoWorkspace: typeof workspace.organisation.isDemoWorkspace === 'boolean' ? workspace.organisation.isDemoWorkspace : false,
          },
          role: workspace.role,
        })),
      };
    }
  } catch {
    // Fall through to clearing bad state.
  }

  clearPersistedPendingWorkspaceSelection();
  return null;
}

function normalizePendingNextPath(nextPath?: string): string {
  return typeof nextPath === 'string' && nextPath.trim() ? nextPath : '/dashboard';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [hasStoredTokens] = useState<boolean>(() => restoreTokensFromStorage());
  const [restoredAuth] = useState<RestoredAuthState>(() => restorePersistedAuthState() ?? emptyAuthState());
  const [user, setUser] = useState<AuthUser | null>(restoredAuth.user);
  const [org, setOrg] = useState<AuthOrg | null>(restoredAuth.org);
  const [role, setRole] = useState<MemberRole | null>(restoredAuth.role);
  const [workspaces, setWorkspaces] = useState<WorkspaceSelectionOption[]>(restoredAuth.workspaces);
  const [partnerAccess, setPartnerAccess] = useState<AuthPartnerAccess | null>(restoredAuth.partnerAccess);
  const [pendingVerification, setPendingVerificationState] = useState<PendingVerificationState | null>(
    () => restorePendingVerification(),
  );
  const [pendingWorkspaceSelection, setPendingWorkspaceSelectionState] = useState<PendingWorkspaceSelectionState | null>(
    () => restorePendingWorkspaceSelection(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(hasStoredTokens);

  const clearPendingVerification = useCallback(() => {
    setPendingVerificationState(null);
    clearPersistedPendingVerification();
  }, []);

  const clearPendingWorkspaceSelection = useCallback(() => {
    setPendingWorkspaceSelectionState(null);
    clearPersistedPendingWorkspaceSelection();
  }, []);

  const setPendingVerification = useCallback((input: PendingVerificationState) => {
    clearTokens();
    clearPersistedAuthState();
    clearPersistedPendingWorkspaceSelection();
    setUser(null);
    setOrg(null);
    setRole(null);
    setWorkspaces([]);
    setPartnerAccess(null);
    setPendingWorkspaceSelectionState(null);
    setPendingVerificationState(input);
    persistPendingVerification(input);
  }, []);

  const setPendingWorkspaceSelection = useCallback((input: PendingWorkspaceSelectionState) => {
    clearTokens();
    clearPersistedAuthState();
    clearPersistedPendingVerification();
    setUser(null);
    setOrg(null);
    setRole(null);
    setWorkspaces([]);
    setPartnerAccess(null);
    setPendingVerificationState(null);
    setPendingWorkspaceSelectionState(input);
    persistPendingWorkspaceSelection(input);
  }, []);

  const establishSession = useCallback((input: {
    user: AuthUser;
    organisation: AuthOrg;
    role: MemberRole;
    workspaces?: WorkspaceSelectionOption[];
    partnerAccess?: AuthPartnerAccess | null;
    tokens: AuthTokenPair;
  }) => {
    const nextWorkspaces = normalizeWorkspaceOptions(input.workspaces, {
      organisation: input.organisation,
      role: input.role,
    });
    setTokens(input.tokens.accessToken, input.tokens.refreshToken, input.organisation.id, input.role);
    persistAuthState(input.user, input.organisation, input.role, input.partnerAccess ?? null, nextWorkspaces);
    setUser(input.user);
    setOrg(input.organisation);
    setRole(input.role);
    setWorkspaces(nextWorkspaces);
    setPartnerAccess(input.partnerAccess ?? null);
    clearPendingVerification();
    clearPendingWorkspaceSelection();
  }, [clearPendingVerification, clearPendingWorkspaceSelection]);

  const syncLocalePreferences = useCallback((input: {
    preferredLocale?: AppLocale | null;
    defaultLocale?: AppLocale | null;
  }) => {
    const nextUser = user && input.preferredLocale !== undefined
      ? { ...user, preferredLocale: input.preferredLocale }
      : user;
    const nextOrg = org && input.defaultLocale !== undefined
      ? { ...org, defaultLocale: input.defaultLocale }
      : org;
    const nextWorkspaces = org && input.defaultLocale !== undefined
      ? workspaces.map((workspace) => (
        workspace.organisation.id === org.id
          ? {
              ...workspace,
              organisation: {
                ...workspace.organisation,
                defaultLocale: input.defaultLocale ?? null,
              },
            }
          : workspace
      ))
      : workspaces;

    setUser(nextUser);
    setOrg(nextOrg);
    setWorkspaces(nextWorkspaces);

    if (nextUser && nextOrg && role) {
      persistAuthState(nextUser, nextOrg, role, partnerAccess, nextWorkspaces);
    }
  }, [org, partnerAccess, role, user, workspaces]);

  const handleAuthFlowResponse = useCallback((data: AuthFlowResponse, nextPath?: string): { status: 'authenticated' | 'verification_required' | 'workspace_selection_required' } => {
    if (data.status === 'authenticated') {
      establishSession(data);
      return { status: 'authenticated' };
    }

    if (data.status === 'workspace_selection_required') {
      setPendingWorkspaceSelection({
        user: data.user,
        selectionToken: data.selectionToken,
        workspaces: data.workspaces,
        nextPath: normalizePendingNextPath(nextPath),
      });
      return { status: 'workspace_selection_required' };
    }

    setPendingVerification({
      user: data.user,
      organisation: data.organisation,
      role: data.role,
      nextPath: normalizePendingNextPath(nextPath),
      verificationEmail: data.verificationEmail,
    });
    return { status: 'verification_required' };
  }, [establishSession, setPendingVerification, setPendingWorkspaceSelection]);

  const login = useCallback(
    async (email: string, password: string, nextPath?: string) => {
      setIsLoading(true);
      try {
        const data = await api.post<AuthFlowResponse>('/auth/login', {
          email,
          password,
        }, { skipAuth: true });
        return handleAuthFlowResponse(data, nextPath);
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthFlowResponse],
  );

  const selectWorkspace = useCallback(async (organisationId: string): Promise<{ status: 'authenticated' | 'verification_required' }> => {
    if (!pendingWorkspaceSelection) {
      throw new Error('No pending workspace selection was found.');
    }

    setIsLoading(true);
    try {
      const data = await api.post<AuthenticatedResponseData | VerificationRequiredResponseData>(
        '/auth/select-workspace',
        {
          selectionToken: pendingWorkspaceSelection.selectionToken,
          organisationId,
        },
        { skipAuth: true },
      );

      const result = handleAuthFlowResponse(data, pendingWorkspaceSelection.nextPath);
      return {
        status: result.status === 'authenticated' ? 'authenticated' : 'verification_required',
      };
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthFlowResponse, pendingWorkspaceSelection]);

  const switchWorkspace = useCallback(async (organisationId: string): Promise<void> => {
    setIsLoading(true);
    try {
      const data = await api.post<AuthenticatedResponseData>(
        '/auth/switch-workspace',
        { organisationId },
      );
      establishSession(data);
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  const createWorkspace = useCallback(async (input: {
    organisationName: string;
    organisationSlug?: string | null;
  }): Promise<void> => {
    setIsLoading(true);
    try {
      const payload = {
        organisationName: input.organisationName,
        ...(input.organisationSlug?.trim()
          ? { organisationSlug: input.organisationSlug.trim() }
          : {}),
      };
      const data = await api.post<AuthenticatedResponseData>(
        '/auth/workspaces',
        payload,
      );
      establishSession(data);
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  const register = useCallback(async (input: {
    email: string;
    fullName: string;
    password: string;
    organisationName: string;
    organisationSlug: string;
    partnerReferralCode?: string | null;
  }, nextPath?: string) => {
    setIsLoading(true);
    try {
      const { partnerReferralCode, ...registerInput } = input;
      const trimmedPartnerReferralCode = partnerReferralCode?.trim();
      const payload = {
        ...registerInput,
        ...(trimmedPartnerReferralCode
          ? { partnerReferralCode: trimmedPartnerReferralCode }
          : {}),
      };

      const data = await api.post<AuthenticatedResponseData | VerificationRequiredResponseData>(
        '/auth/register',
        payload,
        { skipAuth: true },
      );

      if (data.status === 'authenticated') {
        establishSession(data);
        return { status: 'authenticated' as const };
      }

      setPendingVerification({
        user: data.user,
        organisation: data.organisation,
        role: data.role,
        nextPath: normalizePendingNextPath(nextPath),
        verificationEmail: data.verificationEmail,
      });
      return { status: 'verification_required' as const };
    } finally {
      setIsLoading(false);
    }
  }, [establishSession, setPendingVerification]);

  const resendVerificationEmail = useCallback(async (): Promise<VerificationEmailStatus> => {
    if (!pendingVerification) {
      throw new Error('No pending email verification state found.');
    }

    setIsLoading(true);
    try {
      const data = await api.post<ResendVerificationResponse>(
        '/auth/verify-email/resend',
        {
          email: pendingVerification.user.email,
          organisationSlug: pendingVerification.organisation.slug,
        },
        { skipAuth: true },
      );

      const nextVerificationEmail = data.verificationEmail;
      const nextPendingState: PendingVerificationState = {
        ...pendingVerification,
        verificationEmail: nextVerificationEmail,
      };
      setPendingVerificationState(nextPendingState);
      persistPendingVerification(nextPendingState);

      return nextVerificationEmail;
    } finally {
      setIsLoading(false);
    }
  }, [pendingVerification]);

  const confirmEmailVerification = useCallback(async (token: string): Promise<{ nextPath: string }> => {
    setIsLoading(true);
    try {
      const data = await api.post<AuthenticatedResponseData>(
        '/auth/verify-email/confirm',
        { token },
        { skipAuth: true },
      );

      const nextPath = normalizePendingNextPath(pendingVerification?.nextPath);
      establishSession(data);
      return { nextPath };
    } finally {
      setIsLoading(false);
    }
  }, [establishSession, pendingVerification?.nextPath]);

  const logout = useCallback(async () => {
    clearTokens();
    clearPersistedAuthState();
    clearPendingVerification();
    clearPendingWorkspaceSelection();
    setUser(null);
    setOrg(null);
    setRole(null);
    setWorkspaces([]);
    setPartnerAccess(null);
  }, [clearPendingVerification, clearPendingWorkspaceSelection]);

  useEffect(() => {
    if (!hasStoredTokens) {
      setIsBootstrapping(false);
      return;
    }

    let cancelled = false;
    const hydrate = async () => {
      try {
        const data = await api.get<AuthBootstrapResponse>('/auth/me');
        if (cancelled) return;

        const nextWorkspaces = normalizeWorkspaceOptions(data.workspaces, {
          organisation: data.organisation,
          role: data.role,
        });
        setUser(data.user);
        setOrg(data.organisation);
        setRole(data.role);
        setWorkspaces(nextWorkspaces);
        setPartnerAccess(data.partnerAccess);
        persistAuthState(data.user, data.organisation, data.role, data.partnerAccess, nextWorkspaces);
      } catch (error) {
        clearTokens();
        clearPersistedAuthState();
        if (!cancelled) {
          setUser(null);
          setOrg(null);
          setRole(null);
          setWorkspaces([]);
          setPartnerAccess(null);

          if (
            error instanceof ApiError &&
            error.code === 'EMAIL_VERIFICATION_REQUIRED' &&
            restoredAuth.user &&
            restoredAuth.org &&
            restoredAuth.role
          ) {
            const pendingState: PendingVerificationState = {
              user: { ...restoredAuth.user, isEmailVerified: false },
              organisation: restoredAuth.org,
              role: restoredAuth.role,
              nextPath: '/dashboard',
              verificationEmail: {
                deliveryStatus: 'skipped',
                deliveryReason: null,
                expiresAt: null,
              },
            };
            setPendingVerificationState(pendingState);
            persistPendingVerification(pendingState);
          }
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    };

    void hydrate();

    return () => {
      cancelled = true;
    };
  }, [hasStoredTokens, restoredAuth.org, restoredAuth.role, restoredAuth.user]);

  useEffect(() => {
    const nextLocale = user?.preferredLocale
      ?? org?.defaultLocale
      ?? pendingVerification?.user.preferredLocale
      ?? pendingVerification?.organisation.defaultLocale
      ?? pendingWorkspaceSelection?.user.preferredLocale
      ?? resolveInitialLocale();
    void changeAppLanguage(nextLocale);
  }, [
    org?.defaultLocale,
    pendingVerification?.organisation.defaultLocale,
    pendingVerification?.user.preferredLocale,
    pendingWorkspaceSelection?.user.preferredLocale,
    user?.preferredLocale,
  ]);

  const hasSessionToken = hasStoredTokens || hasAccessToken();

  return (
    <AuthContext.Provider
      value={{
        user,
        org,
        role,
        workspaces,
        partnerAccess,
        isAuthenticated: user !== null && user.isEmailVerified && hasSessionToken,
        isLoading,
        isBootstrapping,
        pendingVerification,
        pendingWorkspaceSelection,
        hasPendingVerification: pendingVerification !== null,
        hasPendingWorkspaceSelection: pendingWorkspaceSelection !== null,
        hasPartnerAccess: partnerAccess !== null,
        login,
        register,
        establishSession,
        setPendingVerification,
        setPendingWorkspaceSelection,
        selectWorkspace,
        switchWorkspace,
        createWorkspace,
        resendVerificationEmail,
        confirmEmailVerification,
        clearPendingVerification,
        clearPendingWorkspaceSelection,
        syncLocalePreferences,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
